import { useReducedMotion } from 'framer-motion';

/**
 * Eine Bewegungssprache für die ganze App.
 *
 * **Warum es diesen Hook braucht.** In `App.css` steht ein globaler
 * Kill-Switch für `prefers-reduced-motion`, der jede CSS-Animation auf 0.01ms
 * setzt. Der greift bei framer-motion **nicht**: Das animiert `transform` und
 * `opacity` per JavaScript, also an der CSS-Kaskade vorbei. 49 Dateien in
 * dieser App nutzen framer-motion — und bis August 2026 fragte genau eine
 * (`MainScreen.js`) die Einstellung selbst ab. Für alle anderen bewegte sich
 * die Oberfläche weiter, auch wenn jemand „Bewegung reduzieren" eingeschaltet
 * hatte. Für Menschen mit vestibulärer Störung ist das keine Kleinigkeit.
 *
 * **Die Werte** — überarbeitet im August 2026. Vorher: 180ms, 8px, `easeOut`.
 * Das war unauffällig im doppelten Sinn; die Seite wechselte, ohne dass sich
 * dabei etwas *anfühlte*. Drei Änderungen, alle an dieser einen Stelle:
 *
 *   1. LÄNGER, NICHT LANGSAMER. 260ms statt 180ms, aber mit einer Kurve
 *      (Expo-Out), die sofort losläuft und dann sehr lang ausrollt. Der
 *      Übergang wirkt dadurch schneller als der kurze davor — die gefühlte
 *      Geschwindigkeit hängt am Anfang der Bewegung, nicht an der Dauer.
 *   2. TIEFE STATT NUR VERSATZ. Ein Hauch Skalierung (0.985 → 1) macht aus dem
 *      Heraufschieben ein Herankommen. 1.5% klingt nach nichts und ist genau
 *      der Unterschied zwischen „Text wird sichtbar" und „Karte kommt an".
 *   3. FEDER FÜR MOMENTE. `pop` schwingt am Ende leicht über — für Dinge, die
 *      auftauchen und gefeiert werden dürfen (Bestätigungen, Badges, Pillen).
 *      Nicht für Flächen: Überschwingende Seiten wirken wackelig.
 *
 * Die Kurven sind dieselben, die `index.css` als `--ease-emphasis` und
 * `--ease-spring` führt. Eine Bewegungssprache, zwei Techniken (CSS + JS).
 * Wer sie hier ändert, ändert sie überall — das ist der Punkt.
 *
 * Verwendung:
 *
 *   const { fade, slideUp, duration } = useAppMotion();
 *   <motion.div {...fade}>…</motion.div>
 *
 * Bei aktivem „Bewegung reduzieren" liefert der Hook dieselben Objekte mit
 * Dauer 0, ohne Versatz und ohne Skalierung — der Inhalt erscheint, er fliegt
 * nur nicht.
 */

const DURATION = 0.26;
const OFFSET = 10;
// 1.5% kleiner beim Erscheinen. Mehr wirkt als Zoom-Effekt statt als Tiefe.
const SCALE_FROM = 0.985;

// Spiegelbilder von --ease-emphasis / --ease-spring aus index.css.
export const EASE_EMPHASIS = [0.22, 1, 0.36, 1];
export const EASE_SPRING = [0.34, 1.4, 0.64, 1];

/**
 * Gestaffelte Verzögerungen, ABSICHTLICH krumm.
 *
 * Ein exakt gleichmässiger Takt (n × 40ms) klingt wie ein Metronom und ist
 * genau der Rhythmus, den generierte Oberflächen haben. Die kleinen
 * Unregelmässigkeiten lesen sich wie etwas, das nacheinander eintrudelt — man
 * bemerkt sie nicht bewusst, aber die Liste wirkt weniger maschinell.
 * Dieselbe Folge steht als `.stagger-children` in index.css für die CSS-Seite.
 */
const STAGGER_STEPS = [0, 0.04, 0.095, 0.135, 0.2, 0.245, 0.3, 0.34];
// Ab dem neunten Element ist Schluss: Wer weiter unten steht, wartet sonst eine
// halbe Sekunde auf Inhalt, den er längst sehen will.
const STAGGER_MAX = STAGGER_STEPS[STAGGER_STEPS.length - 1];

export const useAppMotion = () => {
  const reduced = useReducedMotion();

  const duration = reduced ? 0 : DURATION;
  const offset = reduced ? 0 : OFFSET;
  const scaleFrom = reduced ? 1 : SCALE_FROM;
  const transition = { duration, ease: EASE_EMPHASIS };

  return {
    /** true, wenn der Nutzer weniger Bewegung wünscht */
    reduced,
    duration,
    transition,

    /** Reines Ein-/Ausblenden — für Overlays und Wechsel am Ort */
    fade: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition,
    },

    /** Der Standard-Seitenübergang: heraufziehen und einen Hauch herankommen */
    page: {
      initial: { opacity: 0, y: offset, scale: scaleFrom },
      animate: { opacity: 1, y: 0, scale: 1 },
      // Beim Verlassen KEIN Schrumpfen: Die alte Seite soll wegziehen, nicht
      // zusammenfallen — sonst sieht der Wechsel aus wie ein Fehler.
      exit: { opacity: 0, y: -offset },
      transition,
    },

    /** Für Elemente, die von unten hereinkommen (Sheets, Leisten) */
    slideUp: {
      initial: { opacity: 0, y: offset * 2 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: offset * 2 },
      transition,
    },

    /**
     * Der Moment: auftauchen mit leichtem Überschwinger. Für Bestätigungen,
     * Badges, frisch erschienene Pillen — nicht für Flächen und nicht für
     * Listen (dort wird aus dem Charakter schnell Unruhe).
     */
    pop: {
      initial: { opacity: 0, scale: reduced ? 1 : 0.86 },
      animate: { opacity: 1, scale: 1 },
      exit: { opacity: 0, scale: reduced ? 1 : 0.92 },
      transition: { duration, ease: EASE_SPRING },
    },

    /**
     * Chatblase (Okt 2026, wie im Werbespot): federt aus der Ecke ihres
     * Absenders herein — eigene von unten rechts, fremde von unten links.
     * Eine echte Feder statt Kurve: Sie reagiert auf die Hoehe der Blase und
     * fuehlt sich bei kurzen und langen Nachrichten gleich an.
     */
    bubble: (own = false) => ({
      initial: reduced ? { opacity: 0 } : { opacity: 0, y: 14, scale: 0.92 },
      animate: { opacity: 1, y: 0, scale: 1 },
      transition: reduced
        ? { duration: 0 }
        : { type: 'spring', stiffness: 460, damping: 30, mass: 0.9 },
      style: { transformOrigin: own ? '100% 100%' : '0% 100%' },
    }),

    /**
     * Kleines Element ploppt auf (Avatar im Stapel, Haken, Zaehler-Badge).
     * Kraeftiger als `pop`: startet bei 60 % wie im Spot.
     */
    popIn: {
      initial: { opacity: 0, scale: reduced ? 1 : 0.6 },
      animate: { opacity: 1, scale: 1 },
      exit: { opacity: 0, scale: reduced ? 1 : 0.6 },
      transition: { duration: reduced ? 0 : 0.42, ease: EASE_SPRING },
    },

    /**
     * Eine Zeile schiebt sich in eine Liste (neuer Logbucheintrag, aufgeklappte
     * Stufe): Hoehe von 0 auf natuerlich, die Zeilen darunter ruecken weich nach.
     * `overflow: hidden` gehoert an das animierte Element.
     */
    insertRow: {
      initial: reduced ? { opacity: 0 } : { opacity: 0, height: 0 },
      animate: { opacity: 1, height: 'auto' },
      exit: reduced ? { opacity: 0 } : { opacity: 0, height: 0 },
      transition: { duration: reduced ? 0 : 0.5, ease: EASE_EMPHASIS },
    },

    /** `layout`-Prop fuer framer-motion: Umsortieren gleitet, ausser bei reduced. */
    layout: !reduced,

    /**
     * Gestaffeltes Erscheinen einer Liste. `index` ist die Position.
     * Ohne Bewegungswunsch erscheint alles gleichzeitig, ohne Verzögerung.
     */
    listItem: (index = 0) => ({
      initial: { opacity: 0, y: offset, scale: scaleFrom },
      animate: { opacity: 1, y: 0, scale: 1 },
      transition: {
        ...transition,
        delay: reduced ? 0 : (STAGGER_STEPS[index] ?? STAGGER_MAX),
      },
    }),
  };
};

export default useAppMotion;
